import { FormControl, Grid2, InputLabel, MenuItem, Paper, Select, Tooltip } from '@mui/material';
import { GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { Invoice, State } from '@thro-types/billing';

import { useCallback, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { useGetDepartmentsWithInvoicesQuery } from 'api/hooks/billing/department';
import { useGetInvoicesListQuery, useInvoiceMutation } from 'api/hooks/billing/invoices';
import { DataGrid } from 'app/components';
import SimplePDFViewer from 'app/components/PdfPreview';
import { Searchfield } from 'app/components/Searchfield';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { FEATURE_FLAGS } from 'utils/featureFlags';
import { useCurrentURL } from 'utils/hooks/useCurrentURL';
import usePageHeading from 'utils/hooks/usePageHeading';
import useQueryStringState from 'utils/hooks/useQueryStringState';
import { useTableQuery } from 'utils/hooks/useTableQuery';

import { invoiceReadyValidationSchema } from '../MyInvoices/detail/validation';
import { ApproveInvoicesButton, DenyInvoicesButton, AdditionalInvoiceState } from '../components';
import { ApproveDialog } from '../components/ApproveDialog';
import { DenyDialog } from '../components/DenyDialog';
import { useFinanceInvoiceColumns } from '../components/columns';
import { useFetchPdf } from '../hooks';

export const ApproveInvoicesView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedInvoices, setSelectedInvoices] = useState<GridRowSelectionModel>([]);
  const [cancellationReason, setCancellationReason] = useState('');
  const [departmentFilter, setDepartmentFilter] = useQueryStringState<string>('department', '');
  const [isLoading, setIsLoading] = useState(false);

  const { data: departmentsData } = useGetDepartmentsWithInvoicesQuery();

  const {
    search,
    handleSearchTextChange,
    debouncedSearchText,
    page,
    pageSize,
    sortingModel,
    handlePaginationChange,
    handleSortModelChange
  } = useTableQuery({
    defaultSort: [{ field: 'invoiceDate', sort: 'desc' }],
    defaultPageSize: 100
  });

  const { data: invoicesData, isLoading: isLoadingInvoice } = useGetInvoicesListQuery({
    params: {
      state: 'READY',
      department:
        departmentsData && departmentsData.length > 0
          ? departmentsData
              .filter(d => d.id === departmentFilter)
              .map(d => ({ email: d.email, name: d.name }))[0] || null
          : null,
      take: pageSize,
      skip: page * pageSize,
      search: debouncedSearchText,
      sort: sortingModel[0]?.field,
      sortOrder: sortingModel[0]?.sort
    }
  });

  const dispatch = useDispatch();
  const currentURL = useCurrentURL();

  const isPreviewEnabled = useBooleanFlagValue(FEATURE_FLAGS.BILLING.INVOICEPREVIEW_ENABLED, false);

  const { updateState } = useInvoiceMutation();

  const [approveInvoicesOpen, setApproveInvoicesOpen] = useState(false);
  const [denyInvoicesOpen, setDenyInvoicesOpen] = useState(false);

  const { pdfData, open, handleClose, handlePreview } = useFetchPdf();

  usePageHeading(t(translations.pages.billingFinance.approveTitle()));

  const handleSelectionChange = useCallback((newSelectedInvoices: GridRowSelectionModel) => {
    setSelectedInvoices(newSelectedInvoices);
  }, []);

  const handleApproveInvoices = async () => {
    try {
      if (selectedInvoices.length > 0) {
        await updateState.mutateAsync({
          invoiceIds: selectedInvoices.map(id => id.toString()),
          state: 'APPROVED'
        });
      }
      dispatch(
        AlertActions.success(t(translations.pages.billingFinance.mutation.approveSuccessful()))
      );
    } catch {
      dispatch(AlertActions.error(t(translations.pages.billingFinance.mutation.approveFailed())));
    } finally {
      setSelectedInvoices([]);
      setApproveInvoicesOpen(false);
    }
  };

  const handleDenyInvoices = async () => {
    try {
      if (selectedInvoices.length > 0) {
        await updateState.mutateAsync({
          invoiceIds: selectedInvoices.map(id => id.toString()),
          state: 'DENIED'
        });
      }
      dispatch(
        AlertActions.success(t(translations.pages.billingFinance.mutation.denySuccessful()))
      );
    } catch {
      dispatch(AlertActions.error(t(translations.pages.billingFinance.mutation.denyFailed())));
    } finally {
      setSelectedInvoices([]);
      setDenyInvoicesOpen(false);
    }
  };

  const handleRowClick = (params: GridRowParams) => {
    navigate(`/billing/invoices/${params.id}`, {
      state: { back: currentURL }
    });
  };

  const [verifiedInvoices, setVerifiedInvoices] = useState<
    (Omit<Invoice, 'state'> & { isValidInvoice: boolean; state: string })[]
  >([]);

  useLayoutEffect(() => {
    const validateInvoices = async () => {
      if (!invoicesData?.data) return;
      setIsLoading(true);
      const validated = await Promise.all(
        invoicesData.data.map(async invoice => {
          const isValid = await invoiceReadyValidationSchema(t).isValid(invoice);

          return {
            ...invoice,
            state: isValid ? State.READY : AdditionalInvoiceState.INCOMPLETE,
            isValidInvoice: isValid
          };
        })
      );
      setIsLoading(false);
      setVerifiedInvoices(validated);
    };

    validateInvoices();
  }, [invoicesData, t]);

  return (
    <>
      {pdfData && <SimplePDFViewer pdfFilePath={pdfData} open={open} onClose={handleClose} />}
      <Grid2 container justifyContent={'space-between'} gap={1} alignItems="center">
        <Grid2 container gap={1}>
          <FormControl sx={{ minWidth: '16em' }} size="small">
            <InputLabel id="departmentLabel">
              {t(translations.pages.billingFinance.departmentFilter())}
            </InputLabel>
            <Select
              labelId="departmentLabel"
              label={t(translations.pages.billingFinance.departmentFilter())}
              value={departmentFilter}
              onChange={e => setDepartmentFilter(`${e.target.value}`)}
            >
              <MenuItem value={''}>{'----'}</MenuItem>
              {departmentsData &&
                departmentsData.length > 0 &&
                departmentsData?.map(department => (
                  <MenuItem key={department.id} value={department.id}>
                    {department.name} ({department.email})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <Searchfield handleSearch={handleSearchTextChange} search={search} />
        </Grid2>
        <Grid2 container justifyContent={'flex-end'} gap={1.5}>
          <DenyInvoicesButton
            disabled={selectedInvoices.length === 0}
            selectedInvoices={selectedInvoices}
            onClick={() => setDenyInvoicesOpen(true)}
          />
          <Tooltip
            title={
              verifiedInvoices
                .filter(i => selectedInvoices.includes(i.id))
                .some(i => !i.isValidInvoice)
                ? t(translations.pages.billingFinance.approveInvoicesDisabledTooltip())
                : ''
            }
          >
            <span>
              <ApproveInvoicesButton
                disabled={
                  selectedInvoices.length === 0 ||
                  verifiedInvoices
                    .filter(i => selectedInvoices.includes(i.id))
                    .some(i => !i.isValidInvoice)
                }
                selectedInvoices={selectedInvoices}
                onClick={() => setApproveInvoicesOpen(true)}
              />
            </span>
          </Tooltip>
        </Grid2>
        <Grid2 size={12}>
          <Grid2 component={Paper} style={{ height: '80vh' }} container>
            <DataGrid
              checkboxSelection
              sx={{
                '.disabled-row': {
                  backgroundColor: '#fafafa',
                  borderLeft: '2px solid #38a7ce'
                }
              }}
              style={{ height: '100%', width: '100%' }}
              loading={isLoadingInvoice || isLoading}
              columns={useFinanceInvoiceColumns({
                handlePdfPreview: handlePreview,
                disableStateSorting: true
              })}
              rows={verifiedInvoices}
              rowCount={invoicesData?.count || 0}
              getRowClassName={params => {
                if (!params.row.isValidInvoice) {
                  return 'disabled-row';
                }
                return '';
              }}
              paginationMode="server"
              paginationModel={{ page, pageSize }}
              onPaginationModelChange={handlePaginationChange}
              sortingMode="server"
              sortModel={sortingModel}
              onSortModelChange={handleSortModelChange}
              onRowClick={handleRowClick}
              onRowSelectionModelChange={handleSelectionChange}
              rowSelectionModel={selectedInvoices}
              disableColumnMenu
              columnVisibilityModel={{
                outgoingInvoiceNumber: false,
                bookingCode: false,
                approvedAt: false,
                actions: false,
                pdfPreview: isPreviewEnabled
              }}
            />
          </Grid2>
        </Grid2>
      </Grid2>
      <ApproveDialog
        open={approveInvoicesOpen}
        handleClose={() => setApproveInvoicesOpen(false)}
        onConfirm={handleApproveInvoices}
      />
      <DenyDialog
        open={denyInvoicesOpen}
        handleClose={() => setDenyInvoicesOpen(false)}
        onConfirm={handleDenyInvoices}
        cancellationReason={cancellationReason}
        setCancellationReason={setCancellationReason}
      />
    </>
  );
};
